/**
 * `growth/config` — PURE resolution of ads configuration.
 *
 * Same three-layer pattern as monetization kit
 * (`monetization/config.ts`): **code defaults → env → admin UI
 * overrides**. Env and UI share source: env provides default and each override
 * hot-patches ONE key; deleting override returns control to env.
 *
 * **OFF by default is sacred**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the entire
 * layer is inert, `network` is `none`, and no SDK is loaded.
 */

import type { AdContentRating, AdInitOptions, AdsConfig, AdTier, AdTierRegime } from "./types";
import { AD_CONTENT_RATINGS, AD_TIER_REGIMES, AD_TIERS, STRICTEST_AD_TIER_REGIME } from "./types";

/** Backend `none`: no-op. It is DEFAULT and guarantees "no ads, no SDK". */
export const AD_NETWORK_NONE = "none";

/**
 * Backends known to kit. `none` first on purpose.
 * `fake` is deterministic provider for e2e suites (no network or DOM): NEVER
 * configured in a real environment.
 */
export const AD_NETWORKS = [AD_NETWORK_NONE, "adsense", "fake"] as const;

/** Admin UI override keys (same source as env). */
export const AD_OVERRIDE_KEYS = [
  "ads.enabled",
  "ads.network",
  "ads.tier",
  "ads.child_directed",
  "ads.max_content_rating",
  "ads.client_id",
] as const;

export type AdOverrideKey = (typeof AD_OVERRIDE_KEYS)[number];

export function isValidAdOverrideKey(key: string): key is AdOverrideKey {
  return (AD_OVERRIDE_KEYS as readonly string[]).includes(key);
}

/** Code defaults: EVERYTHING off. Enabling ads is an explicit decision. */
export function builtinAdsDefaults(): AdsConfig {
  return {
    enabled: false,
    network: AD_NETWORK_NONE,
    tier: "A",
    childDirected: false,
    maxAdContentRating: "G",
    clientId: "",
  };
}

function parseBool(v: string | undefined): boolean | undefined {
  if (v === undefined) {
    return undefined;
  }
  const s = v.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(s)) {
    return false;
  }
  return undefined;
}

function isAdTier(v: string): v is AdTier {
  return (AD_TIERS as readonly string[]).includes(v);
}

function parseTier(v: string | undefined): AdTier | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdTier(s) ? s : undefined;
}

function isAdContentRating(v: string): v is AdContentRating {
  return (AD_CONTENT_RATINGS as readonly string[]).includes(v);
}

function parseRating(v: string | undefined): AdContentRating | undefined {
  const s = v?.trim().toUpperCase();
  return s && isAdContentRating(s) ? s : undefined;
}

/**
 * The STRICTEST of two ratings. `AD_CONTENT_RATINGS` is ordered from most
 * strict to most lenient, so "strictest" = lower index.
 */
export function strictestAdContentRating(a: AdContentRating, b: AdContentRating): AdContentRating {
  return AD_CONTENT_RATINGS.indexOf(a) <= AD_CONTENT_RATINGS.indexOf(b) ? a : b;
}

/**
 * Declared regime for a tier. An unknown tier falls back to STRICTEST
 * regime (fail-closed): without a declared policy, no advertising is served.
 */
export function adTierRegime(tier: string): AdTierRegime {
  return isAdTier(tier) ? AD_TIER_REGIMES[tier] : STRICTEST_AD_TIER_REGIME;
}

/**
 * Layer 2: env. Both forms used in monorepo are accepted — `ADS_*` (server and
 * e2e suites) and `VITE_ADS_*` (web bundle) — with Vite winning on client,
 * which is where build presides.
 */
export function applyAdsEnv(config: AdsConfig, env: Record<string, string | undefined>): void {
  const enabled = parseBool(env.VITE_ADS_ENABLED) ?? parseBool(env.ADS_ENABLED);
  if (enabled !== undefined) {
    config.enabled = enabled;
  }
  const network = (env.VITE_ADS_NETWORK ?? env.ADS_NETWORK)?.trim().toLowerCase();
  if (network) {
    config.network = network;
  }
  const tier = parseTier(env.VITE_ADS_TIER ?? env.ADS_TIER);
  if (tier) {
    config.tier = tier;
  }
  const child = parseBool(env.VITE_ADS_CHILD_DIRECTED) ?? parseBool(env.ADS_CHILD_DIRECTED);
  if (child !== undefined) {
    config.childDirected = child;
  }
  const rating = parseRating(env.VITE_ADS_MAX_CONTENT_RATING ?? env.ADS_MAX_CONTENT_RATING);
  if (rating) {
    config.maxAdContentRating = rating;
  }
  const clientId = (env.VITE_ADS_CLIENT_ID ?? env.ADS_CLIENT_ID)?.trim();
  if (clientId) {
    config.clientId = clientId;
  }
}

/** Layer 3: back-office override (same semantics as monetization). */
export function applyAdsOverride(config: AdsConfig, key: string, value: unknown): void {
  switch (key) {
    case "ads.enabled": {
      if (typeof value === "boolean") {
        config.enabled = value;
      }
      return;
    }
    case "ads.network": {
      if (typeof value === "string" && value.trim()) {
        config.network = value.trim().toLowerCase();
      }
      return;
    }
    case "ads.tier": {
      const tier = parseTier(typeof value === "string" ? value : undefined);
      if (tier) {
        config.tier = tier;
      }
      return;
    }
    case "ads.child_directed": {
      if (typeof value === "boolean") {
        config.childDirected = value;
      }
      return;
    }
    case "ads.max_content_rating": {
      const rating = parseRating(typeof value === "string" ? value : undefined);
      if (rating) {
        config.maxAdContentRating = rating;
      }
      return;
    }
    case "ads.client_id": {
      if (typeof value === "string") {
        config.clientId = value.trim();
      }
      return;
    }
    default:
  }
}

/**
 * **Layer 4 — regime clamp. Classification wins over flag.**
 *
 * Clamps resolved configuration to declared FLOOR of its tier
 * (`AD_TIER_REGIMES`). Only hardens: no env variable, back-office
 * override, or oversight can relax a tier regime below
 * its minimum. Property of the KIT, not individual app — if habits
 * changes tier tomorrow, it inherits regime without code changes.
 *
 * Guarantees:
 *   - ad-free tiers (`C`, `D`): `enabled=false` and `network=none` ⇒ zero SDK;
 *   - child tiers (`K`): `childDirected=true` ALWAYS;
 *   - rating ceiling per tier, and **`G` whenever child regime applies**,
 *     wherever it comes from (from tier or env HARDENING a tier `A`).
 *
 * Idempotent and monotonic: applying twice gives same result, and never raises ceiling.
 *
 * History: C/D clamp existed from day one, but child tier clamp
 * did not — `VITE_ADS_CHILD_DIRECTED=false` was enough to serve behavioral
 * ads to minors. Discovered by `kid-games-app` (only tier K app) and
 * generalized into canon so no clone relies on remembering it. And caution with
 * UNKNOWN tier: prior habits version checked `tier === "C" || tier === "D"`,
 * so corrupt tier (raw back-office JSON) served ads without policy; now falls
 * back to strictest regime.
 */
export function clampAdsConfigToTier(config: AdsConfig): void {
  const regime = adTierRegime(config.tier);
  if (!regime.adsAllowed) {
    config.enabled = false;
    config.network = AD_NETWORK_NONE;
  }
  if (regime.childDirected) {
    config.childDirected = true;
  }
  // Tier ceiling and child regime ceiling combine: lowest wins.
  const ceiling = config.childDirected
    ? strictestAdContentRating(regime.maxAdContentRating, "G")
    : regime.maxAdContentRating;
  config.maxAdContentRating = strictestAdContentRating(config.maxAdContentRating, ceiling);
}

/** Full resolution: defaults → env → overrides → regime clamp. */
export function resolveAdsConfig(
  env: Record<string, string | undefined>,
  overrides: readonly { key: string; value: unknown }[] = [],
): AdsConfig {
  const config = builtinAdsDefaults();
  applyAdsEnv(config, env);
  for (const o of overrides) {
    applyAdsOverride(config, o.key, o.value);
  }
  clampAdsConfigToTier(config);
  return config;
}

/**
 * Init options HARDENED against resolved config.
 *
 * Second loophole of same family: `WebAdProvider.init(opts)` accepted
 * `opts.childDirected` as-is, allowing callers to unwind regime
 * clamp just guaranteed. Here caller can only **harden**
 * (request child-directed, or lower rating); relaxing is impossible.
 */
export function hardenAdInitOptions(config: AdsConfig, opts: AdInitOptions = {}): AdInitOptions {
  const childDirected = config.childDirected || opts.childDirected === true;
  const requested = opts.maxAdContentRating ?? config.maxAdContentRating;
  let rating = strictestAdContentRating(config.maxAdContentRating, requested);
  if (childDirected) {
    rating = strictestAdContentRating(rating, "G");
  }
  return {
    childDirected,
    maxAdContentRating: rating,
    // Does not affect policy: passes as-is.
    ...(opts.testDeviceIds ? { testDeviceIds: opts.testDeviceIds } : {}),
  };
}

/**
 * Should this app load ads kit? Combines ads config with verdict
 * from monetization kit (`shouldShowAds(config, tier)` presides: premium ⇒ 0 ads
 * immediately). Without `enabled` or with `none` network, **zero SDK**.
 */
export function adsAreLive(config: AdsConfig, monetizationSaysAds: boolean): boolean {
  return config.enabled && config.network !== AD_NETWORK_NONE && monetizationSaysAds;
}
